import React, { useState, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { Button } from "react-bootstrap";

const TraineeExamComponent = ({ examStart, history }) => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const roomRef = useRef(null);
  const videoPreviewRef = useRef(null);

  useEffect(() => {
    try {
      const persistedAuth = window.localStorage?.getItem("persist:tagnet-auth");
      if (persistedAuth) {
        const parsed = JSON.parse(persistedAuth);
        const userData = parsed.user ? JSON.parse(parsed.user) : null;
        setUser(userData);
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }, []);

  const getTraineeToken = async () => {
    try {
      const persistedAuth = window.localStorage?.getItem("persist:tagnet-auth");
      if (!persistedAuth) {
        throw new Error("Authentication not found");
      }
      const parsed = JSON.parse(persistedAuth);
      const authToken = parsed.authToken ? JSON.parse(parsed.authToken) : null;

      if (!authToken) {
        throw new Error("Auth token not found");
      }

      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${authToken}`);

      const response = await fetch(`${baseUrl}/api/get_theory_rtc_token`, {
        method: "POST",
        headers: myHeaders,
        redirect: "follow",
      });
      const result = await response.json();
      if (result.status) {
        return result;
      } else {
        throw new Error("No trainee room found in response");
      }
    } catch (error) {
      console.error("❌ Error fetching token:", error);
      throw error;
    }
  };

  const startExamMonitoring = async () => {
    if (connected || connecting) {
      console.log("Already connected or connecting");
      return;
    }

    setConnecting(true);
    setError("");

    try {
      console.log("🔗 Starting exam monitoring...");

      // Get token
      const roomInfo = await getTraineeToken();
      const { room: roomName, token, identity } = roomInfo;

      console.log("📝 Room info:", { roomName, identity });

      const serverUrl = "https://webrtc.tagindia.co.in";

      // Create room
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: {
            width: 1280,
            height: 720,
            frameRate: 30,
          },
        },
      });

      roomRef.current = room;

      // Connect to room
      await room.connect(serverUrl, token);
      console.log(`✅ Connected to room: ${roomName}`);

      // Enable camera
      try {
        await room.localParticipant.setCameraEnabled(true);
        console.log("✅ Camera enabled");

        // Attach video to preview (small, hidden)
        const videoTrack = Array.from(
          room.localParticipant.videoTrackPublications.values(),
        ).find((pub) => pub.track && pub.kind === Track.Kind.Video);

        if (videoTrack?.track && videoPreviewRef.current) {
          videoTrack.track.attach(videoPreviewRef.current);
          console.log("📹 Video preview attached");
        }
      } catch (err) {
        console.error("⚠️ Camera not available:", err.message);
        setError("Camera access denied. Please allow camera access.");
        throw err;
      }

      // Enable microphone
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        console.log("✅ Microphone enabled");
      } catch (err) {
        console.warn("⚠️ Microphone not available:", err.message);
      }

      // Room event listeners
      room.on(RoomEvent.Disconnected, () => {
        console.log("❌ Disconnected from room");
        setConnected(false);
        setError("Disconnected from monitoring. Please rejoin.");
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.log("🔄 Reconnecting...");
        setError("Connection lost. Reconnecting...");
      });

      room.on(RoomEvent.Reconnected, () => {
        console.log("✅ Reconnected");
        setError("");
      });

      setConnected(true);
      setConnecting(false);
      console.log("✅ Exam monitoring started successfully");
    } catch (error) {
      console.error("❌ Failed to start monitoring:", error);
      setError(`Failed to start monitoring: ${error.message}`);
      setConnecting(false);
      setConnected(false);

      // Cleanup on error
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    }
  };

  const stopExamMonitoring = () => {
    if (roomRef.current) {
      console.log("🛑 Stopping exam monitoring...");
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    setConnected(false);
    setError("");
  };

  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, []);

  return (
    <>
      <Button
        id="acceptbtn"
        style={{ float: "right" }}
        onClick={() => {
          examStart();
          startExamMonitoring();
          history.push("/exam/questions");
        }}
      >
        I Accept
      </Button>

      <div
        style={{
          opacity: 0.01,
          width: "80px",
          height: "60px",
          overflow: "hidden",
          position: "absolute",
          bottom: "-100px",
        }}
      >
        <video
          ref={videoPreviewRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    </>
  );
};

export default TraineeExamComponent;
